/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx, css } from "@emotion/core";
import { Formik, Field, FieldArray, getIn } from "formik";
import { Redirect } from "react-router";
import _last from "lodash/last";
import _get from "lodash/get";
import _find from "lodash/find";
import _includes from "lodash/includes";

import { get, post } from "../api";

import PrimaryCTA from "../../components/PrimaryCTA";
import PrimaryCTAButton from "../../components/PrimaryCTA";
import SecondaryCTA from "../../components/SecondaryCTA";
import Form from "../../components/Form";
import {
    FormikGCSEGradePicker,
    FormikSubjectPicker,
} from "../../components/Input";
import RemoveButton from "../../components/RemoveButton";
import RadioButton from "../../components/RadioButton";
import RadioGroup from "../../components/RadioGroup";
import HorizontalDivider from "../../components/HorizontalDivider";
import { useModal } from "../../components/Modal/useModal";
import { submitGradesInterceptorModal } from '../../Apply/index';


import PageContainer from "../components/PageContainer";
import { colors } from "../../theme";
import { resourceUrls } from "../../config";
import { handleGet, handlePost } from "../api/handler";

const initialValues = {
    gradesType: "oldGrades",
    subjects: [
        {
            subject: null,
            grades: [],
            numberOfGrades: 1,
        },
    ],
};

const getGCSESubjects = async ({
    setIsLoggedIn,
    setIsLoading,
    setError,
    setSubjects,
}) => {
    await handleGet({
        getApiCall: () =>
            get({ url: resourceUrls.gcseQualifications.getSubjects }),
        setIsLoggedIn,
        setIsLoading,
        setError,
        setData: setSubjects,
        dataTransform: res => _get(res, "subjects"),
    });
};

const getGCSEGrades = async ({
    setIsLoggedIn,
    setIsLoading,
    setError,
    setGrades,
}) => {
    await handleGet({
        getApiCall: () =>
            get({ url: resourceUrls.qualifications.getGrades("gcse") }),
        setIsLoggedIn,
        setIsLoading,
        setError,
        setData: setGrades,
    });
};

const buildQualificationsBody = (
    { values: { gradesType, subjects } } = {
        values: { hasAdditionalQualifications: "oldGrades", subjects: [] },
    }
) => {
    const gcseQualifications = subjects.map(subject => ({
        id: _get(subject, "subject.value"),
        grades: _get(subject, "grades").map(grade => _get(grade, "value")),
    }));

    const body = {
        subjects: gcseQualifications,
    };

    return body;
};

const submitGCSEQualifications = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async (values, actions) => {
    const body = buildQualificationsBody({ values });

    await handlePost({
        postApiCall: () =>
            post({
                url: resourceUrls.gcseQualifications.submit,
                body,
            }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const submitEmptyGCSEs = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async () => {
    const body = {
        subjects: [],
    };

    await handlePost({
        postApiCall: () =>
            post({
                url: resourceUrls.gcseQualifications.submit,
                body,
            }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });
};

const GCSEs = () => {
    const [subjects, setSubjects] = useState([]);
    const [grades, setGrades] = useState({
        newGrades: [],
        oldGrades: [],
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    const { openModal } = useModal();

    useEffect(() => {
        getGCSESubjects({
            setIsLoading,
            setIsLoggedIn,
            setError,
            setSubjects,
        });
    }, []);

    useEffect(() => {
        getGCSEGrades({
            setIsLoading,
            setIsLoggedIn,
            setError,
            setGrades,
        });
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            isLoggedIn={isLoggedIn}
        >
            <h2>Which of these subjects did you study at GCSE?</h2>
            <p>
                We take all of your grades into account for your application, so
                please add as many as you have.
            </p>
            <Formik
                initialValues={initialValues}
                onSubmit={submitGCSEQualifications(
                    setIsLoggedIn,
                    setError,
                    setFormSubmissionComplete
                )}
                render={({ isSubmitting, ...props}) => (
                    <Form onSubmit={props.handleSubmit}>
                        {/* <p>{JSON.stringify(props.values)}</p> */}
                        <GradeTypeSelector>
                            <h4>My grading system is:</h4>
                            <RadioGroup direction="row">
                                <Field
                                    component={RadioButton}
                                    name="gradesType"
                                    id="oldGrades"
                                    data-testid="oldGrades-radio-button"
                                    label="A*-G"
                                />
                                <Field
                                    component={RadioButton}
                                    name="gradesType"
                                    id="newGrades"
                                    data-testid="newGrades-radio-button"
                                    label="9-1"
                                />
                            </RadioGroup>
                            <DidntTakeGCSEs
                                handleClick={submitEmptyGCSEs(
                                    setIsLoggedIn,
                                    setError,
                                    setFormSubmissionComplete
                                )}
                            />
                        </GradeTypeSelector>
                        <div
                            css={css`
                                margin-top: 0px;
                            `}
                        ></div>
                        <FieldArray
                            name="subjects"
                            render={arrayHelpers => (
                                <>
                                    {props.values.subjects.map(
                                        (subject, index) => (
                                            <>
                                                <FormikSubjectPicker
                                                    subjects={subjects}
                                                    index={index}
                                                    touched={props.touched}
                                                    errors={props.errors}
                                                />
                                                <FormikGCSEGradePicker
                                                    grades={
                                                        props.values
                                                            .gradesType ===
                                                        "newGrades"
                                                            ? grades.newGrades
                                                            : grades.oldGrades
                                                    }
                                                    index={index}
                                                    touched={props.touched}
                                                    errors={props.errors}
                                                    handleBlur={
                                                        props.handleBlur
                                                    }
                                                    doubleAward={
                                                        _get(
                                                            props.values
                                                                .subjects[index]
                                                                .subject,
                                                            "label"
                                                        ) ===
                                                        "Science (Double Award)"
                                                    }
                                                />
                                                {index > 0 && (
                                                    <RemoveButton
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            arrayHelpers.remove(
                                                                index
                                                            );
                                                            // required to stop Formik validating
                                                            // untouched subjects
                                                            return props.setFieldTouched(
                                                                "subjects",
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        Remove subject
                                                    </RemoveButton>
                                                )}
                                                <HorizontalDivider />
                                            </>
                                        )
                                    )}
                                    <PrimaryCTA
                                        type="button"
                                        disabled={
                                            _get(
                                                _last(props.values.subjects),
                                                "subject"
                                            ) === null ||
                                            _get(
                                                _last(props.values.subjects),
                                                "grades"
                                            ).length < 1
                                        }
                                        onClick={e => {
                                            e.preventDefault();
                                            arrayHelpers.push({
                                                subject: null,
                                                grades: [],
                                            });
                                            // required to stop Formik validating the new subject
                                            // as soon as it's added
                                            return props.setFieldTouched(
                                                "subjects",
                                                false
                                            );
                                        }}
                                    >
                                        Add another
                                    </PrimaryCTA>
                                </>
                            )}
                        />
                        <PrimaryCTAButton
                            type="button"
                            disabled={isSubmitting}
                            data-testid="next-button"
                            onClick={async () => {
                                const validation = await props.validateForm();
                                if (validation.subjects) {
                                    // Programmatically submit the form to display the errors.
                                    return props.submitForm();
                                }
                                openModal(submitGradesInterceptorModal(props))
                            }}>
                            Next →
                        </PrimaryCTAButton>
                    </Form>
                )}
            />
        </PageContainer>
    );
};

const DidntTakeGCSEs = ({ handleClick }) => (
    <button
        type="button"
        onClick={handleClick}
        data-testid="didnt-take-gcses"
        css={css`
            color: ${colors.info6};
            background: none;
            border: none;
            padding: 0;
            height: 22px;
            margin-left: 10px;
            font-size: 16px;
            display: inline;
            &:hover {
                cursor: pointer;
                color: ${colors.info5};
            }
        `}
    >
        I didn't take GCSEs
    </button>
);

const GradeTypeSelector = ({ children }) => (
    <div
        css={css`
            display: flex;
            flex-direction: row;
            & h4 {
                margin-right: 8px;
            }
        `}
    >
        {children}
    </div>
);

export default GCSEs;
